import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRollbar } from '@rollbar/react';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  type BatteryLeadFormData,
  submitBatteryLeadToGoogle,
} from '@/clients/googleSheetClient';
import ActionModal from '@/components/ActionModal/ActionModal';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import { RadioGroup } from '@/components/BaseLibrary/RadioGroup/RadioGroup';
import LeadFormContainer from '@/components/Form/LeadFormContainer/LeadFormContainer';
import { LeadFormTCPA } from '@/components/Form/LeadFormTCPA/LeadFormTCPA';
import FormResultDialog, {
  type DialogContent,
} from '@/components/FormResultDialog/FormResultDialog';
import type { GoogleUrls } from '@/constants/baseUrls';
import { getFormType, LeadFormType } from '@/enums/FormType';
import useEligibility, { EligibilityType } from '@/hooks/useEligibility';
import {
  type LeadFormInputData,
  useSubmitLeadToZeus,
} from '@/hooks/useSubmitLeadToZeus';
import useGlobalStyles from '@/styles/global.styles';
import type { ActionModalCF } from '@/types';
import { getErrorMessage, getErrorObject } from '@/utils/getErrorMessage';
import { EMAIL_REGEX } from '@/utils/validation/constants';
import {
  firstNameValidator,
  honeypotAddressFieldValidator,
  InputName,
  lastNameValidator,
  OwnHome,
  ownHomeValidator,
  phoneValidator,
  zipCodeValidator,
} from '@/utils/validation/inputs';
import useStyles from './LeadForm.styles';
import LeadFormContent from './LeadFormContent';

export enum LeadFormStyle {
  ACTION_USER_INPUT = 'actionUserInput',
  HERO = 'hero',
}

export enum FormOrientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export enum DialogType {
  SUCCESS = 'success',
  FAILURE = 'failure',
}

const FORM_SUBMIT_ERROR_MESSAGE =
  'There was an error submitting the form, please try again';

const baseLeadFormSchema = z.object({
  ...firstNameValidator,
  ...lastNameValidator,
  [InputName.EMAIL]: z.string(),
  ...zipCodeValidator,
  ...phoneValidator,
  ...ownHomeValidator,
  ...honeypotAddressFieldValidator,
});

export type LeadFormSchemaType = z.infer<typeof baseLeadFormSchema>;

interface LeadFormProps {
  campaignId?: string;
  heading?: string;
  ownerQuestion?: string;
  submitButtonText?: string;
  tcpa: RichTextDocument;
  successDialogContent?: DialogContent | ActionModalCF;
  failDialogContent?: DialogContent | ActionModalCF;
  orientation?: FormOrientation;
  isOnDark?: boolean;
  googleSheetUrls?: GoogleUrls;
  formType?: LeadFormType;
  overrideCampaignId?: boolean;
  leadFormstyle?: LeadFormStyle;
  hideEmailInput?: boolean;
  redirectUrl?: string;
  outOfMarketRedirectUrl?: string;
}

export function getDialogType(
  type: string,
  isEligible: boolean | null,
  isFailDialogAvailable?: boolean
): DialogType {
  return type === LeadFormType.ZEUS && isFailDialogAvailable && !isEligible
    ? DialogType.FAILURE
    : DialogType.SUCCESS;
}

export function LeadForm({
  campaignId: contentfulCampaignId,
  heading,
  ownerQuestion,
  submitButtonText,
  tcpa,
  successDialogContent,
  failDialogContent,
  orientation = FormOrientation.VERTICAL,
  isOnDark = false,
  googleSheetUrls,
  formType,
  overrideCampaignId,
  leadFormstyle = LeadFormStyle.ACTION_USER_INPUT,
  hideEmailInput = false,
  redirectUrl,
  outOfMarketRedirectUrl,
}: LeadFormProps) {
  const leadFormSchema = baseLeadFormSchema.superRefine((data, ctx) => {
    if (hideEmailInput) {
      return;
    }
    // Check if email is provided
    if (!data[InputName.EMAIL] || data[InputName.EMAIL].length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'This is a required field',
        path: [InputName.EMAIL],
      });
      return;
    }

    // Check if email format is valid
    if (!EMAIL_REGEX.test(data[InputName.EMAIL])) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please enter a valid email address',
        path: [InputName.EMAIL],
      });
    }
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitSuccessful, isValid },
    trigger,
  } = useForm<LeadFormSchemaType>({
    resolver: zodResolver(leadFormSchema),
    mode: 'onTouched',
    defaultValues: {
      [InputName.OWN_HOME]: OwnHome.OWN,
      [InputName.EMAIL]: '',
    },
  });
  const router = useRouter();
  const { classes, cx } = useStyles({ isOnDark });
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { submitLeadToZeus } = useSubmitLeadToZeus();
  const { fetchEligibility } = useEligibility();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<'success' | 'failure'>();
  const rollbar = useRollbar();

  const isVertical = orientation === FormOrientation.VERTICAL;

  const type = getFormType(formType);

  const OwnHomeRadios = (
    <RadioGroup
      legend={isVertical ? ownerQuestion : undefined}
      options={[
        { label: 'Yes', value: OwnHome.OWN },
        { label: 'No', value: OwnHome.RENT },
      ]}
      trigger={trigger}
      register={register}
      name={InputName.OWN_HOME}
      errors={errors}
      isOnDark={!isVertical && isOnDark}
      className={cx(
        isVertical &&
          (leadFormstyle === LeadFormStyle.HERO
            ? classes.radioGroupHero
            : classes.radioGroup)
      )}
      // biome-ignore lint/complexity/noUselessTernary: Automated migration to Biome
      isSmall={leadFormstyle === LeadFormStyle.HERO ? true : false}
    />
  );

  // biome-ignore lint/correctness/noNestedComponentDefinitions: Automated migration to Biome
  const LeadFormDialog = () => {
    const dialogContent =
      dialogType === DialogType.FAILURE
        ? failDialogContent
        : successDialogContent;
    if (!dialogType || !dialogContent) return;

    if ('sys' in dialogContent) {
      return (
        <ActionModal
          open={showDialog}
          onClose={() => setShowDialog(false)}
          modalContent={dialogContent}
        />
      );
    }

    return (
      <FormResultDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        dialogContent={dialogContent}
      />
    );
  };

  const onSubmit: SubmitHandler<LeadFormSchemaType> = async (
    data: LeadFormSchemaType
  ) => {
    const { firstName, lastName, zipCode, phone, ownHome, address, email } =
      data;
    let dialogTypeResult: DialogType;

    try {
      const isEligible = fetchEligibility(EligibilityType.WEBSITE, zipCode);
      dialogTypeResult = getDialogType(type, isEligible, !!failDialogContent);
      setDialogType(dialogTypeResult);

      if (type === LeadFormType.ZEUS) {
        const zeusLeadFormData: LeadFormInputData = {
          first_name: firstName,
          last_name: lastName,
          email: hideEmailInput ? '' : email,
          zip: zipCode,
          phone_home: phone,
          property_ownership: ownHome as OwnHome,
        };

        await submitLeadToZeus({
          zeusData: zeusLeadFormData,
          contentfulCampaignId,
          gtmSubmitEventFormType: 'Standard Lead Form',
          overrideCampaignId,
          honeypot: Boolean(address),
        });
      } else if (type === LeadFormType.GOOGLE_SHEETS) {
        if (googleSheetUrls) {
          const batteryLeadFormData: BatteryLeadFormData = {
            firstName,
            lastName,
            email,
            phone,
            zip: zipCode,
          };
          await submitBatteryLeadToGoogle({
            batteryData: batteryLeadFormData,
            googleSheetUrls,
            propertyOwnership: ownHome,
            honeypot: Boolean(address),
          });
        }
      } else {
        throw new Error('Invalid type provided.');
      }
    } catch (err: unknown) {
      rollbar.error(getErrorMessage(err), getErrorObject(err));
      setError('root.formSubmitError', {
        type: 'formSubmit',
        message: FORM_SUBMIT_ERROR_MESSAGE,
      });
      return;
    }

    if (redirectUrl) {
      const params = new URLSearchParams({
        email,
        firstName,
        lastName,
        zipCode,
        phone,
      });

      if (dialogTypeResult === DialogType.FAILURE && outOfMarketRedirectUrl) {
        router.push(`${outOfMarketRedirectUrl}?${params}`);
      } else {
        router.push(`${redirectUrl}?${params}`);
      }
    } else {
      setShowDialog(true);
    }
  };

  return (
    <LeadFormContainer
      className={cx(
        isVertical &&
          (leadFormstyle === LeadFormStyle.HERO
            ? classes.formHero
            : classes.form)
      )}
    >
      <LeadFormDialog />
      <h2 className={cx(globalClasses.h3, classes.formHeading)}>{heading}</h2>
      {leadFormstyle === LeadFormStyle.ACTION_USER_INPUT && (
        <span className={cx(globalClasses.caption, classes.formSubHeading)}>
          All fields are required.
        </span>
      )}
      <LeadFormContent
        isOnDark={isOnDark}
        isVertical={isVertical}
        classes={classes}
        globalClasses={globalClasses}
        ownHomeRadios={OwnHomeRadios}
        contentStyle={classes.formContent}
        // biome-ignore lint/complexity/noUselessTernary: Automated migration to Biome
        evenSpacing={leadFormstyle === LeadFormStyle.HERO ? true : false}
        register={register}
        errors={errors}
        hideEmailInput={hideEmailInput}
      />
      <LeadFormTCPA
        text={tcpa}
        className={
          leadFormstyle === LeadFormStyle.HERO ? classes.tcpaHero : classes.tcpa
        }
      />
      {errors?.root?.formSubmitError && (
        <p className={classes.submitError}>
          {errors.root.formSubmitError.message}
        </p>
      )}
      <div
        className={cx(
          isVertical
            ? classes.buttonContainer
            : classes.horizontalButtonContainer
        )}
      >
        <Button
          type='submit'
          variant={ButtonVariant.PRIMARY}
          label={submitButtonText}
          size={ButtonSize.MD}
          onClick={handleSubmit(onSubmit)}
          className={
            isVertical
              ? leadFormstyle === LeadFormStyle.HERO
                ? classes.buttonHero
                : classes.button
              : classes.horizontalButton
          }
          disabled={
            Object.keys(errors).length > 0 || isSubmitSuccessful || !isValid
          }
          isOnDark={isOnDark}
        />
      </div>
    </LeadFormContainer>
  );
}
